import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock } from "lucide-react";
import { BlogLayout } from "@/components/BlogLayout";
import { BLOG_POSTS } from "@/data/blogPosts";
import { useSeo, SITE_URL } from "@/lib/seo";

export default function BlogListPage() {
  const [featured, ...rest] = BLOG_POSTS;

  useSeo({
    title: "The Not By Will Blog — Faith-Based Help to Quit Nicotine",
    description:
      "Encouraging, practical articles on quitting smoking and vaping — Bible verses, craving help, withdrawal timelines, and grace for starting over.",
    path: "/blog",
    type: "website",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "Not By Will Blog",
      url: `${SITE_URL}/blog`,
      description:
        "Faith-based, practical help to quit smoking and vaping — rooted in grace, not shame.",
      blogPost: BLOG_POSTS.map((p) => ({
        "@type": "BlogPosting",
        headline: p.title,
        url: `${SITE_URL}/blog/${p.slug}`,
        datePublished: p.date,
        dateModified: p.updated,
        description: p.description,
      })),
    },
  });

  return (
    <BlogLayout testid="blog-list-page">
      <main className="px-6 sm:px-10 pb-16">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="pt-14 pb-10 border-b border-black/5">
            <p className="text-xs uppercase tracking-[0.2em] text-brand-teal-dark font-semibold mb-3">The Journal</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
              Help to quit — <span className="text-brand-teal-dark">by His strength.</span>
            </h1>
            <p className="text-lg text-stone mt-5 max-w-2xl leading-relaxed">
              Honest, practical encouragement for quitting nicotine — grounded in psychology and Scripture,
              and free of shame. Read a little; then take one small step.
            </p>
          </div>

          {/* Featured */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="pt-10"
          >
            <Link
              to={`/blog/${featured.slug}`}
              data-testid="blog-featured-card"
              className="group grid md:grid-cols-2 gap-6 md:gap-8 items-center rounded-3xl overflow-hidden border border-black/5 hover:border-brand-teal/40 transition-colors"
            >
              <div className="aspect-[16/11] md:aspect-auto md:h-full overflow-hidden">
                <img
                  src={featured.hero}
                  alt={featured.heroAlt}
                  loading="eager"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                />
              </div>
              <div className="p-6 md:pr-8 md:py-8">
                <div className="flex items-center gap-3 text-xs text-stone mb-3">
                  <span className="rounded-full bg-brand-teal/10 text-brand-teal-dark px-2.5 py-1 font-semibold">{featured.category}</span>
                  <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {featured.readMin} min read</span>
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight leading-snug group-hover:text-brand-teal-dark transition-colors">
                  {featured.title}
                </h2>
                <p className="text-stone mt-3 leading-relaxed">{featured.excerpt}</p>
                <span className="inline-flex items-center gap-1.5 text-brand-teal-dark font-semibold mt-5 text-sm">
                  Read article <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </Link>
          </motion.div>

          {/* Grid */}
          <div className="grid sm:grid-cols-2 gap-6 md:gap-8 pt-10">
            {rest.map((p, i) => (
              <motion.div
                key={p.slug}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.05 * i }}
              >
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="group flex flex-col rounded-3xl overflow-hidden border border-black/5 hover:border-brand-teal/40 transition-colors h-full"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={p.hero}
                      alt={p.heroAlt}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 text-xs text-stone mb-2.5">
                      <span className="rounded-full bg-brand-teal/10 text-brand-teal-dark px-2.5 py-1 font-semibold">{p.category}</span>
                      <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {p.readMin} min</span>
                    </div>
                    <h3 className="font-heading text-xl font-semibold tracking-tight leading-snug group-hover:text-brand-teal-dark transition-colors">
                      {p.title}
                    </h3>
                    <p className="text-sm text-stone mt-2.5 leading-relaxed flex-1">{p.excerpt}</p>
                    <span className="inline-flex items-center gap-1.5 text-brand-teal-dark font-semibold mt-4 text-sm">
                      Read <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-16 rounded-3xl bg-brand-navy text-brand-ivory p-8 sm:p-12 text-center">
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">
              Ready to stop reading and start walking it out?
            </h2>
            <p className="text-brand-ivory-muted mt-3 max-w-xl mx-auto leading-relaxed">
              Not By Will builds a gentle, personalized plan in minutes — with a free craving tool and a
              community praying with you. Free to begin.
            </p>
            <Link
              to="/"
              data-testid="blog-list-cta"
              className="inline-flex items-center gap-2 rounded-full bg-brand-teal text-brand-navy px-6 py-3.5 font-semibold mt-6 hover:bg-brand-teal/90 transition-colors"
            >
              Start your plan free <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </main>
    </BlogLayout>
  );
}
